/* eslint-disable no-loop-func */
// == Import npm
import React, { useState, useEffect } from "react";
import { Button, Modal, message, Progress } from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import QRCode from "qrcode";
import classNames from "classnames";
import {
  associateKeyCard,
  getKeyCardOwner,
  markBadgeCheckin,
  markBadgeCheckout,
  printBadge,
} from "services/features/VisitsSlice";
import { Link } from "react-router-dom";
import { usePromptModal } from "components/Modal/PromptModal";
import { useConfirmModal } from "components/Modal/ConfirmModal";

// == Import SCSS
import "../modalPrint.less";
import "./Badge.less";

// == icons
// == import i18n
import { useTranslation } from "react-i18next";

// == assets
import { ReactComponent as PrintCheck } from "assets/illustrations/printCheck.svg";

const Badge = ({
  // visiteur actuellement sélectionné
  visitor,
  // list des visiteurs à imprimer
  visitorCheck,
  // id du visiteur courant
  currentUser,
  setCurrent,
  setVisible,
  setSearch,
  setVisitorCheck,
  setVerifyIdentityDisplayed,
  verifyIdentityDisplayed,
  isDisabled,
  setDisabled,
  mustCheckIn,
  disablePrinter,
  useKeyCard,
  limitToRegister = false,
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [askUserInput, PromptModal] = usePromptModal();
  const [askUserConfirm, ConfirmModal] = useConfirmModal();

  const [qrcode, setQrcode] = useState(null);
  const [startPrint, setStartPrint] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [printVisible, setPrintVisible] = useState(true);
  const [visit, setVisit] = useState();

  const progress = ((currentUser + 1) / visitorCheck.length) * 100;

  useEffect(() => {
    getFakeQRCode();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const visit = visitorCheck[currentUser];
    setVisit(visit);
    // eslint-disable-next-line
  }, [currentUser]);

  const getFakeQRCode = () => {
    let stringToEncode = ["00000000000"].join("::");
    QRCode.toDataURL(
      stringToEncode,
      { errorCorrectionLevel: "L", width: 200 },
      function (err, url) {
        if (qrcode !== url) {
          setQrcode(url);
        }
      },
    );
  };

  const print = async () => {
    try {
      const visitor = visitorCheck[currentUser];
      const badgeContent = await dispatch(printBadge(visitor));
      if (badgeContent?.error && badgeContent.error === "cancelled") {
        message.warn(t("VisitCancelled"));
        setStartPrint(false);
        setCurrent(0);
        setSearch("");
        setVisitorCheck([]);
        setVisible(false);
        return;
      }
      if (mustCheckIn) {
        await dispatch(markBadgeCheckin(badgeContent));
      }
      if (useKeyCard && !limitToRegister) {
        let badgeRef;
        let canceled = false;

        while (!badgeRef && !canceled) {
          badgeRef = await askUserInput(t("BadgeNumber"), t("EnterBadgeNumber"));

          if (!badgeRef) {
            canceled = true;
          } else {
            const { owner } = await dispatch(getKeyCardOwner(badgeRef));
            if (owner && owner.id !== visitor.id) {
              if (
                (await askUserConfirm(
                  t("BadgeAlreadyUsed"),
                  t("BadgeAlreadyUsedText", { ...owner, reference: badgeRef }),
                )) === false
              ) {
                badgeRef = null;
              }
            }
          }
        }
        if (!canceled) await dispatch(associateKeyCard(badgeContent, badgeRef));
      }
    } catch (e) {
      message.error(t(e.message));
      if (visitorCheck.length > 0) message.warn(t("PrintBadgesStopped"));
    }

    const current = currentUser + 1;
    if (current < visitorCheck.length) setCurrent(current);
    else {
      setStartPrint(false);
      setCurrent(0);
      setSearch("");
      setVisitorCheck([]);
      setVisible(false);
    }
  };
  useEffect(() => {
    if (startPrint) print();
    // eslint-disable-next-line
  }, [startPrint, currentUser]);

  const handleDisabled = () => {
    setDisabled(!isDisabled);
  };

  const handleDisplay = () => {
    setVerifyIdentityDisplayed(!verifyIdentityDisplayed);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div className="badge-print-container">
      {PromptModal}
      {ConfirmModal}
      <div className="badge" id="printArea">
        <div className="badge__topLeft">
          <div className="badge__topLeft__v">X</div>

          <div className="badge__topLeft__date">
            {moment(visit?.startDate).format("DD/MM/YYYY")}
            {/* {moment(visit.startDate).format("HH:mm")} */}
          </div>
        </div>
        <div className="badge__topRight">
          <div className="badge__topRight__firstname">{visitor.firstname}</div>
          <div className="badge__topRight__lastname"> {visitor.lastname}</div>
          <div className="badge__topRight__company"> {visitor.company}</div>
        </div>
        <div className="badge__bottomLeft">
          {visit?.resident && (
            <div className="badge__bottomLeft__resident">
              Resident : {visit.resident.lastname} {visit.resident.firstname}
            </div>
          )}
        </div>
        <div className="badge__bottomRight">
          <div className="badge__bottomRight__qrCode">
            <img src={qrcode} alt="" />
          </div>
        </div>
      </div>
      <div
        className={classNames("badge--blur", {
          "badge--blur--hide": !verifyIdentityDisplayed || !mustCheckIn,
        })}
      >
        {visitorCheck.length === 1 ? (
          <>
            <p className="badge__title">{t("VerifyIdentity")}</p>
            <p className="badge__text">
              {t("CheckIdentitie")} <br /> {t("PrintingBadge")}
            </p>
          </>
        ) : (
          <p className="badge__text">
            <p className="badge__title">{t("VerifyIdentities")}</p>
            {t("CheckIdentities")} <br /> {t("PrintingBadges")}
          </p>
        )}

        <Button
          type="primary"
          size="large"
          onClick={() => {
            handleDisabled();
            handleDisplay();
          }}
        >
          {visitorCheck.length === 1 ? t("ValidIdentie") : t("ValidIdenties")}
        </Button>
      </div>
      <div
        style={{
          marginTop: "50px",
          alignItems: "center",
          display: "flex",
          flexFlow: "column wrap",
        }}
      >
        <Button
          type="primary"
          size="large"
          //disabled={isDisabled && mustCheckIn}
          className={classNames("btn__next", {
            "btn__next--disabled": isDisabled && mustCheckIn,
          })}
          onClick={() => {
            setStartPrint(true);
            setDisabled(true);
            setPrintVisible(false);
          }}
        >
          {disablePrinter
            ? visitorCheck.length > 1
              ? t("GiveBadges")
              : t("GiveBadge")
            : visitorCheck.length > 1
            ? t("PrintBadges")
            : t("PrintBadge")}
        </Button>
        <p
          className={classNames("text__print", {
            "text__print--display": !printVisible,
          })}
        >
          {disablePrinter ? "" : t("PrintProgress")}
          {disablePrinter ? "" : <Progress percent={progress} status="active" showInfo={false} />}
        </p>
      </div>

      <Modal
        open={isModalVisible}
        width="700px"
        className="modalConfirm"
        footer={[
          <Button
            type="primary"
            size="large"
            className="modalConfirm__finish"
            style={{ width: "300px" }}
            onClick={handleOk}
            onBlur={() => {
              // Because on mobile, Ant-d has a problem with onscreen keyboard and webview which puh the screen when the keyboard is deployed
              window.scrollTo(0, 0);
            }}
          >
            {t("Finish")}
          </Button>,
          <Button type="default" size="large" style={{ width: "300px" }}>
            <Link
              to="/"
              onClick={handleOk}
              onBlur={() => {
                // Because on mobile, Ant-d has a problem with onscreen keyboard and webview which puh the screen when the keyboard is deployed
                window.scrollTo(0, 0);
              }}
            >
              {t("BackHome")}
            </Link>
          </Button>,
        ]}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        {" "}
        {visitorCheck.length > 1 ? (
          <>
            <PrintCheck />
            <p className="modalConfirm__text">{t("TheResidentHasJustBeenNotified")}</p>
          </>
        ) : (
          <>
            <PrintCheck />
            <p className="modalConfirm__text">{t("TheResidentHasJustBeenNotified2")}</p>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Badge;
